import { Action, createReducer, on } from '@ngrx/store';
import { AccountConnectionStatus, AccountSyncStatus, IAccount, isMailboxSyncQueued, MailboxType } from '../models/account.models';
import { ISmartInboxState, ISmartInboxStateType, InboxFolderType, IFolderKPI } from '../models/inbox.models';
import { IConversation, IConversationDetails, ConversationClassification, ConversationStatus, ISentMessage } from '../models/conversation.models';
import { IDraft } from '../models/draft.models';
import { IMention } from '../models/mention.models';
import { IPagination } from 'src/app/shared/models/page.model';
import * as smartInboxActions from './smart-inbox.actions';
import {
  IConversationTimelineObject,
  isTimelineMessage,
  isTimelineOrderPlaceholder,
  ITimelineOrderPlaceholder,
  ORDER_PLACEHOLDER_ID,
  TimelineObjectType,
} from '../models/timeline.models';

// ─── Filter Interface ────────────────────────────────────────────────────────

export interface IInboxFilter {
  activeAccounts?: IAccount[];
  categories?: ConversationClassification[];
  statuses?: ConversationStatus[];
  assigneeIds?: number[];
  /**
   * Team ids: threads assigned to any member of ANY of these teams (the union of their
   * members).
   *
   * Absent/`undefined` when unset, never `[]`. The old `undefined`-never-`0` warning is
   * moot for an array, but the reason behind it is not: the backend branches on the raw
   * argument's PRESENCE, so a present-but-empty value means "matches nothing" rather than
   * "no filter". `toHttpParams` does drop an empty array, so `[]` would not actually reach
   * the wire — but relying on that makes the builder's intent unreadable without going
   * and checking, so "unset" is spelled `undefined` all the way through.
   */
  teamIds?: number[];
  searchTerm?: string;
  from?: string;
  to?: string;
  subject?: string;
  includesWords?: string;
  excludesWords?: string;
  dateFrom?: string;
  dateTo?: string;
  hasAttachment?: boolean;
  /** Thread's effective (override-aware) urgency. */
  isUrgent?: boolean;
  customerIds?: number[];
  selectedCustomers?: { id: number; displayName: string }[];
}

// ─── Loading State ───────────────────────────────────────────────────────────

export interface SmartInboxLoadingState {
  get: {
    accounts: boolean;
    conversations: boolean;
    conversationDetails: boolean;
    mentions: boolean;
  };
  create: {
    aiDraft: boolean;
  };
  update: {
    accountName: boolean;
    accountType: boolean;
    accountRefresh: boolean;
    accountDisconnect: boolean;
    accountDelete: boolean;
    accountReconnect: boolean;
    conversation: boolean;
    markRead: boolean;
    /** Ids of messages whose classification override is in flight. */
    messageClassificationIds: number[];
    threadUrgency: boolean;
  };
}

// ─── State Interface ─────────────────────────────────────────────────────────

export interface SmartInboxState {
  connectionState: ISmartInboxStateType;
  accounts: IAccount[];
  filter: IInboxFilter;
  activeFolder: InboxFolderType;
  foldersKPIs: IFolderKPI[];
  conversations: IConversation[];
  pagination: IPagination | null;
  activeConversation: IConversation | null;
  activeConversationDetails: IConversationDetails | null;
  selectedConversationIds: number[];
  drafts: IDraft[];
  mentions: IMention[];
  /** Sent folder: flat list of outbound messages (not threads). */
  sentMessages: ISentMessage[];
  /** Selected comment id sent with conversation details endpoint (mention id = comment id). Cleared after details load. */
  activeCommentId: number | null;
  /** Message id to scroll-to and highlight after details load (set when opening a sent message). Cleared after highlight. */
  activeMessageId: number | null;
  loading: SmartInboxLoadingState;
}

// ─── Initial State ───────────────────────────────────────────────────────────

export const initialSmartInboxState: SmartInboxState = {
  connectionState: ISmartInboxState.INITIAL,
  accounts: [],
  filter: {},
  activeFolder: InboxFolderType.ALL,
  foldersKPIs: [],
  conversations: [],
  pagination: null,
  activeConversation: null,
  activeConversationDetails: null,
  selectedConversationIds: [],
  drafts: [],
  mentions: [],
  sentMessages: [],
  activeCommentId: null,
  activeMessageId: null,
  loading: {
    get: {
      accounts: false,
      conversations: false,
      conversationDetails: false,
      mentions: false,
    },
    create: {
      aiDraft: false,
    },
    update: {
      accountName: false,
      accountType: false,
      accountRefresh: false,
      accountDisconnect: false,
      accountDelete: false,
      accountReconnect: false,
      conversation: false,
      markRead: false,
      messageClassificationIds: [],
      threadUrgency: false,
    },
  },
};

// ─── Create-order placeholder ───────────────────────────────────────────────

const hasOrderPlaceholder = (details: IConversationDetails): boolean =>
  !!details.objects?.some(isTimelineOrderPlaceholder);

const ORDER_PLACEHOLDER: ITimelineOrderPlaceholder = {
  object_type: TimelineObjectType.ORDER_PLACEHOLDER,
  id: ORDER_PLACEHOLDER_ID,
};

/** Append the skeleton unconditionally (idempotent) — the optimistic add on click. */
const addOrderPlaceholder = (details: IConversationDetails): IConversationDetails =>
  hasOrderPlaceholder(details)
    ? details
    : { ...details, objects: [...(details.objects ?? []), ORDER_PLACEHOLDER] };

/**
 * Re-arm the skeleton from the server's `order_creation_in_progress` flag.
 * Runs on every details load, so it survives a refresh (the flag is derived
 * from the DB claim, not front-end memory) — unlike the old in-memory list,
 * which was lost on reload while the back-end claim lived on (skeleton gone,
 * yet a re-click still hit 409).
 */
const withOrderPlaceholder = (
  details: IConversationDetails | null,
): IConversationDetails | null =>
  !details || !details.order_creation_in_progress ? details : addOrderPlaceholder(details);

const withoutOrderPlaceholder = (
  details: IConversationDetails | null,
  threadId: number,
): IConversationDetails | null => {
  if (!details || details.id !== threadId || !hasOrderPlaceholder(details)) return details;
  return { ...details, objects: details.objects!.filter((obj) => !isTimelineOrderPlaceholder(obj)) };
};

// ─── Shared Inbox (always present in accounts list) ─────────────────────────

export const SHARED_INBOX: IAccount = {
  id: -1,
  name: 'Shared with me',
  email: '',
  provider: null,
  mailbox_type: MailboxType.SHARED,
  mailbox_owner_id: null,
  connection_status: AccountConnectionStatus.CONNECTED,
  sync_status: null,
  last_sync_at: null,
  last_sync_error: null,
  unread_count: null,
  total_threads: null,
  created_at: null,
  default_signature_new: null,
  default_signature_reply_forward: null,
};

function ensureSharedInbox(accounts: IAccount[]): IAccount[] {
  if (accounts.some((a) => a.id === -1)) return accounts;
  return [...accounts, SHARED_INBOX];
}

// ─── Reducer ─────────────────────────────────────────────────────────────────

const featureReducer = createReducer(
  initialSmartInboxState,

  // Connection State
  on(smartInboxActions.SetConnectionState, (state, { connectionState }) => ({
    ...state,
    connectionState,
  })),

  // ─── Active Folder ──────────────────────────────────────────────────────

  on(smartInboxActions.SetActiveFolder, (state, { folder }) => ({
    ...state,
    activeFolder: folder,
  })),

  // ─── Accounts (Load) ──────────────────────────────────────────────────────

  on(smartInboxActions.LoadAccounts, (state) => ({
    ...state,
    loading: { ...state.loading, get: { ...state.loading.get, accounts: true } },
  })),

  on(smartInboxActions.LoadAccountsSuccess, (state, { accounts }) => {
    const hasRealAccounts = accounts.data.length > 0;
    const hasSharedItems = state.foldersKPIs.some(
      (kpi) => kpi.id === InboxFolderType.ALL && kpi.total > 0
    );
    return {
      ...state,
      accounts: ensureSharedInbox(accounts.data),
      connectionState: (hasRealAccounts || hasSharedItems)
        ? ISmartInboxState.CONNECTED
        : ISmartInboxState.CONNECT,
      loading: { ...state.loading, get: { ...state.loading.get, accounts: false } },
    };
  }),

  on(smartInboxActions.LoadAccountsError, (state) => ({
    ...state,
    connectionState: ISmartInboxState.SERVICE_ERROR,
    loading: { ...state.loading, get: { ...state.loading.get, accounts: false } },
  })),

  // ─── Accounts (Add New) ───────────────────────────────────────────────────

  on(smartInboxActions.AddNewAccountSuccess, (state, { accounts }) => ({
    ...state,
    accounts: ensureSharedInbox([...accounts, ...state.accounts]),
  })),

  // ─── Accounts (Update) ────────────────────────────────────────────────────

  on(smartInboxActions.UpdateAccount, (state, { payload }) => ({
    ...state,
    loading: {
      ...state.loading,
      update: {
        ...state.loading.update,
        accountName: 'name' in payload,
        accountType: 'mailbox_type' in payload,
      },
    },
  })),

  on(smartInboxActions.UpdateAccountSuccess, (state, { account }) => ({
    ...state,
    accounts: state.accounts.map((a) => (a.id === account.id ? { ...a, ...account } : a)),
    conversations: state.conversations.map((c) => (c.mailbox.id === account.id ? { ...c, mailbox: { ...c.mailbox, name: account.name } } : c)),
    activeConversation: state.activeConversation?.mailbox.id === account.id ? { ...state.activeConversation, mailbox: { ...state.activeConversation?.mailbox, name: account.name } } : state.activeConversation,
    activeConversationDetails: state.activeConversationDetails?.mailbox.id === account.id ? { ...state.activeConversationDetails, mailbox: { ...state.activeConversationDetails?.mailbox, name: account.name } } : state.activeConversationDetails,
    loading: {
      ...state.loading,
      update: { ...state.loading.update, accountName: false, accountType: false },
    },
  })),

  on(smartInboxActions.UpdateAccountError, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      update: { ...state.loading.update, accountName: false, accountType: false },
    },
  })),

  // ─── Accounts (Refresh) ───────────────────────────────────────────────────

  on(smartInboxActions.RefreshAccounts, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      update: { ...state.loading.update, accountRefresh: true },
    },
  })),

  on(smartInboxActions.RefreshAccountsSuccess, (state, { results }) => {
    // Mirror the claim the route already committed for every queued mailbox. Not optimism:
    // `POST /mailboxes/sync` sets sync_status = 'syncing' synchronously before answering
    // 202, so this is the state the server is already in. Without it the banner has a hole
    // between the 202 and LoadAccountsSuccess — the hole the request-in-flight flag used to
    // paper over, at the cost of also showing "syncing" for syncs that were refused.
    const queued = new Set(results.filter(isMailboxSyncQueued).map((row) => row.mailbox_id));
    return {
      ...state,
      accounts: state.accounts.map((account) => (queued.has(account.id)
        ? { ...account, sync_status: AccountSyncStatus.SYNCING, last_sync_error: null }
        : account)),
      loading: {
        ...state.loading,
        update: { ...state.loading.update, accountRefresh: false },
      },
    };
  }),

  // ─── Accounts (live sync status off SSE) ──────────────────────────────────

  on(smartInboxActions.MailboxSyncStatusChanged, (state, { mailboxId, syncStatus, lastSyncError, totalThreads }) => ({
    ...state,
    // The `.map` no-ops for an unknown mailbox on purpose: the event can arrive for a
    // mailbox this user cannot see (a stale stream, or one disconnected in another tab),
    // and building an IAccount out of an event payload would drop a mailbox with no
    // email/provider into the sidebar.
    accounts: state.accounts.map((account) => (account.id === mailboxId
      ? {
          ...account,
          sync_status: syncStatus,
          // Written on every transition, including the clear. `selectInboxSyncState` reads
          // it only on errored accounts, so a stale value is invisible until the next
          // failure — when it would surface the wrong sentence.
          last_sync_error: lastSyncError,
          total_threads: totalThreads,
        }
      : account)),
  })),

  on(smartInboxActions.RefreshAccountsError, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      update: { ...state.loading.update, accountRefresh: false },
    },
  })),

  // ─── Accounts (Disconnect — reversible) ───────────────────────────────────

  on(smartInboxActions.DisconnectAccount, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      update: { ...state.loading.update, accountDisconnect: true },
    },
  })),

  /**
   * Patches the account, never removes it: this used to `filter` it out, which is why a
   * disconnect looked like a deletion. `activeConversation` is left alone on purpose — the
   * mail stays readable — and clearing the sync fields stops the error banner flashing
   * before the follow-up LoadAccounts lands.
   */
  on(smartInboxActions.DisconnectAccountSuccess, (state, { accountId }) => ({
    ...state,
    accounts: state.accounts.map((a) =>
      a.id === accountId
        ? {
          ...a,
          connection_status: AccountConnectionStatus.DISCONNECTED,
          sync_status: AccountSyncStatus.IDLE,
          last_sync_error: null,
        }
        : a
    ),
    loading: {
      ...state.loading,
      update: { ...state.loading.update, accountDisconnect: false },
    },
  })),

  on(smartInboxActions.DisconnectAccountError, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      update: { ...state.loading.update, accountDisconnect: false },
    },
  })),

  // ─── Accounts (Delete — permanent) ────────────────────────────────────────

  on(smartInboxActions.DeleteAccount, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      update: { ...state.loading.update, accountDelete: true },
    },
  })),

  /**
   * Removes the account for real, and prunes it from the frozen `filter.activeAccounts`
   * snapshot where it would otherwise linger as a filter until the sidebar re-dispatched.
   */
  on(smartInboxActions.DeleteAccountSuccess, (state, { accountId }) => {
    const accounts = state.accounts.filter((a) => a.id !== accountId);
    const activeConversation =
      state.activeConversation?.mailbox.id === accountId ? null : state.activeConversation;
    const activeConversationDetails =
      activeConversation === null ? null : state.activeConversationDetails;

    return {
      ...state,
      accounts,
      activeConversation,
      activeConversationDetails,
      filter: {
        ...state.filter,
        activeAccounts: (state.filter.activeAccounts ?? []).filter((a) => a.id !== accountId),
      },
      loading: {
        ...state.loading,
        update: { ...state.loading.update, accountDelete: false },
      },
    };
  }),

  on(smartInboxActions.DeleteAccountError, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      update: { ...state.loading.update, accountDelete: false },
    },
  })),

  // ─── Accounts (Re-connect) ────────────────────────────────────────────────

  on(smartInboxActions.ReconnectAccount, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      update: { ...state.loading.update, accountReconnect: true },
    },
  })),

  /** PENDING, not CONNECTED: the callback finishes server-side after the popup closes. */
  on(smartInboxActions.ReconnectAccountSuccess, (state, { accountId }) => ({
    ...state,
    accounts: state.accounts.map((a) =>
      a.id === accountId
        ? { ...a, connection_status: AccountConnectionStatus.PENDING, last_sync_error: null }
        : a
    ),
    loading: {
      ...state.loading,
      update: { ...state.loading.update, accountReconnect: false },
    },
  })),

  on(smartInboxActions.ReconnectAccountError, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      update: { ...state.loading.update, accountReconnect: false },
    },
  })),

  // ─── Accounts (KPI) ──────────────────────────────────────────────────────

  on(smartInboxActions.LoadFoldersKPIsSuccess, (state, { kpis }) => {
    const hasRealAccounts = state.accounts.some((a) => a.id !== -1);
    return {
      ...state,
      foldersKPIs: kpis,
      connectionState: hasRealAccounts
        ? state.connectionState
        : (kpis.find((kpi) => kpi.id === InboxFolderType.ALL)?.total === 0
            ? ISmartInboxState.CONNECT
            : ISmartInboxState.CONNECTED),
    };
  }),
  on(smartInboxActions.LoadFoldersKPIsError, (state) => ({
    ...state,
    foldersKPIs: [],
  })),

  on(smartInboxActions.PatchFolderUnreadKPIs, (state, { folder }) => ({
    ...state,
    foldersKPIs: state.foldersKPIs.map((kpi) => (kpi.id === folder ? { ...kpi, unread: Math.max(0, kpi.unread - 1) } : kpi)),
  })),

  // ─── Filters ──────────────────────────────────────────────────────────────

  on(smartInboxActions.SetFilter, (state, { filter }) => ({
    ...state,
    filter,
  })),

  // ─── Conversations (Load) ─────────────────────────────────────────────────

  on(smartInboxActions.LoadConversations, (state) => ({
    ...state,
    conversations: [],
    drafts: [],
    mentions: [],
    sentMessages: [],
    pagination: null,
    selectedConversationIds: [],
    loading: { ...state.loading, get: { ...state.loading.get, conversations: true } },
  })),

  on(smartInboxActions.LoadConversationsSuccess, (state, { conversations }) => ({
    ...state,
    conversations: conversations.data,
    pagination: conversations.metadata,
    loading: { ...state.loading, get: { ...state.loading.get, conversations: false } },
  })),

  on(smartInboxActions.LoadConversationsError, (state) => ({
    ...state,
    loading: { ...state.loading, get: { ...state.loading.get, conversations: false } },
  })),

  /**
   * No request was sent, so empty both lists and drop the spinner — otherwise the view
   * keeps the previous mailbox's rows and spins forever.
   */
  on(smartInboxActions.SkipThreadListLoad, (state) => ({
    ...state,
    conversations: [],
    sentMessages: [],
    pagination: null,
    loading: { ...state.loading, get: { ...state.loading.get, conversations: false } },
  })),

  // ─── Conversations (Load More) ────────────────────────────────────────────

  on(smartInboxActions.LoadMoreConversationsSuccess, (state, { conversations }) => ({
    ...state,
    conversations: [...state.conversations, ...conversations.data],
    pagination: conversations.metadata,
  })),

  // ─── Active Conversation ──────────────────────────────────────────────────

  on(smartInboxActions.SelectConversation, (state, { conversation }) => ({
    ...state,
    activeConversation: conversation,
    activeConversationDetails: null,
    activeCommentId: null,
    activeMessageId: null,
    loading: { ...state.loading, get: { ...state.loading.get, conversationDetails: true } },
  })),

  on(smartInboxActions.LoadConversationFromUrl, (state) => ({
    ...state,
    activeConversation: null,
    activeConversationDetails: null,
    activeMessageId: null,
    loading: { ...state.loading, get: { ...state.loading.get, conversationDetails: true } },
  })),

  // Details can land for a thread the user has already left: the standalone
  // load effect does not cross-cancel with the selection one, so a background
  // refetch (order_created) races an explicit click. Drop the stale response
  // instead of rendering thread A's timeline under thread B's header.
  on(smartInboxActions.LoadConversationDetailsSuccess, (state, { details }) => {
    if (state.activeConversation && details.id !== state.activeConversation.id) return state;
    const mentionId = state.activeCommentId;
    const mentions =
      mentionId != null
        ? state.mentions.map((m) => (m.id === mentionId ? { ...m, is_read: true } : m))
        : state.mentions;
    // The thread rollup (classification / categories / urgency) is recomputed
    // server-side from its messages, so mirror it onto the list row and the
    // active conversation on every detail fetch.
    const rollup = {
      classification: details.classification,
      categories: details.categories,
      is_urgent: details.is_urgent,
    };

    return {
      ...state,
      conversations: state.conversations.map((c) => c.id === details.id ? { ...c, ...rollup, is_read: details.is_read } : c),
      activeConversation: state.activeConversation
        ? {
            ...state.activeConversation,
            ...rollup,
            is_read: details.is_read,
            customer: details.customer ?? null,
          }
        : null,
      // Re-arm the skeleton straight from the server's in-progress flag, so it
      // survives a refresh (the flag is the DB claim, not front-end memory).
      activeConversationDetails: withOrderPlaceholder(details),
      mentions,
      loading: {
        ...state.loading,
        get: { ...state.loading.get, conversationDetails: false },
      },
    };
  }),

  on(smartInboxActions.ClearActiveCommentId, (state) => ({
    ...state,
    activeCommentId: null,
  })),

  on(smartInboxActions.LoadConversationDetailsError, (state) => ({
    ...state,
    loading: { ...state.loading, get: { ...state.loading.get, conversationDetails: false } },
  })),

  on(smartInboxActions.ClearActiveConversation, (state) => ({
    ...state,
    activeConversation: null,
    activeConversationDetails: null,
    activeCommentId: null,
    activeMessageId: null,
  })),

  // ─── Conversation Selection ───────────────────────────────────────────────

  on(smartInboxActions.ToggleConversationSelection, (state, { conversationId }) => {
    const selected = state.selectedConversationIds.includes(conversationId)
      ? state.selectedConversationIds.filter((id) => id !== conversationId)
      : [...state.selectedConversationIds, conversationId];
    return { ...state, selectedConversationIds: selected };
  }),

  on(smartInboxActions.SetConversationsSelection, (state, { conversationIds, selected }) => {
    const touched = new Set(conversationIds);
    const remaining = state.selectedConversationIds.filter((id) => !touched.has(id));

    return {
      ...state,
      selectedConversationIds: selected ? [...remaining, ...conversationIds] : remaining,
    };
  }),

  on(smartInboxActions.ToggleAllConversationsSelection, (state) => {
    // Compared by id, not by count. A length check reports "all selected" for
    // any selection that merely happens to be the same size as the page, which
    // a range selection makes easy to produce.
    const allSelected =
      state.conversations.length > 0 &&
      state.conversations.every((c) => state.selectedConversationIds.includes(c.id));

    return {
      ...state,
      selectedConversationIds: allSelected ? [] : state.conversations.map((c) => c.id),
    };
  }),

  on(smartInboxActions.ClearSelection, (state) => ({
    ...state,
    selectedConversationIds: [],
  })),

  // ─── Conversations (Update) ───────────────────────────────────────────────

  on(smartInboxActions.UpdateConversations, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      update: { ...state.loading.update, conversation: true },
    },
  })),

  on(smartInboxActions.UpdateConversationsSuccess, (state, { conversationIds, payload, updatedDetails }) => {
    const detailsById = new Map(
      conversationIds.map((id, i) => [id, updatedDetails[i]])
    );

    const applyDetailsToConversation = (c: IConversation, details: IConversationDetails): IConversation => ({
      ...c,
      is_read: details.is_read,
      classification: details.classification ?? c.classification,
      status: details.status !== undefined ? details.status : c.status,
      assignee: details.assignee !== undefined ? details.assignee : c.assignee,
      customer: details.customer !== undefined ? details.customer : c.customer,
      mailbox: details.mailbox ?? c.mailbox,
    });

    const applyDetailsToMention = (m: IMention, details: IConversationDetails): IMention => ({
      ...m,
      classification: details.classification ?? m.classification,
      status: details.status !== undefined ? details.status : m.status,
      assignee: details.assignee !== undefined ? details.assignee : m.assignee,
    });

    const activeConversation =
      state.activeConversation && detailsById.has(state.activeConversation.id)
        ? applyDetailsToConversation(state.activeConversation, detailsById.get(state.activeConversation.id)!)
        : state.activeConversation;

    const activeDetails = state.activeConversation && detailsById.get(state.activeConversation.id);
    const activeConversationDetails =
      state.activeConversationDetails && activeDetails
        ? {
            ...state.activeConversationDetails,
            is_read: activeDetails.is_read,
            classification: activeDetails.classification ?? state.activeConversationDetails.classification,
            status: activeDetails.status !== undefined ? activeDetails.status : state.activeConversationDetails.status,
            assignee: activeDetails.assignee !== undefined ? activeDetails.assignee : state.activeConversationDetails.assignee,
            customer: activeDetails.customer,
            mailbox: activeDetails.mailbox ?? state.activeConversationDetails.mailbox,
          }
        : state.activeConversationDetails;

    return {
      ...state,
      conversations: state.conversations.map((c) => {
        const details = detailsById.get(c.id);
        return details ? applyDetailsToConversation(c, details) : c;
      }),
      mentions: state.mentions.map((m) => {
        const details = detailsById.get(m.thread_id);
        return details ? applyDetailsToMention(m, details) : m;
      }),
      activeConversation,
      activeConversationDetails,
      loading: {
        ...state.loading,
        update: { ...state.loading.update, conversation: false },
      },
    };
  }),

  on(smartInboxActions.UpdateConversationsError, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      update: { ...state.loading.update, conversation: false },
    },
  })),

  // ─── Message Classification (category / intent override) ──────────────────

  on(smartInboxActions.UpdateMessageClassification, (state, { messageId }) => ({
    ...state,
    loading: {
      ...state.loading,
      update: {
        ...state.loading.update,
        messageClassificationIds: [...state.loading.update.messageClassificationIds, messageId],
      },
    },
  })),

  on(smartInboxActions.UpdateMessageClassificationSuccess, (state, { messageId, result }) => ({
    ...state,
    // Patch the message in place; the thread rollup arrives with the re-fetch
    // dispatched by the effect.
    activeConversationDetails: state.activeConversationDetails
      ? {
          ...state.activeConversationDetails,
          objects: state.activeConversationDetails.objects?.map((o) =>
            isTimelineMessage(o) && Number(o.id) === messageId
              ? { ...o, classification: result.classification, intent: result.intent }
              : o
          ),
        }
      : state.activeConversationDetails,
    loading: {
      ...state.loading,
      update: {
        ...state.loading.update,
        messageClassificationIds: state.loading.update.messageClassificationIds.filter((id) => id !== messageId),
      },
    },
  })),

  on(smartInboxActions.UpdateMessageClassificationError, (state, { messageId }) => ({
    ...state,
    loading: {
      ...state.loading,
      update: {
        ...state.loading.update,
        messageClassificationIds: state.loading.update.messageClassificationIds.filter((id) => id !== messageId),
      },
    },
  })),

  // ─── Thread Urgency (operator override) ───────────────────────────────────

  on(smartInboxActions.UpdateThreadUrgency, (state) => ({
    ...state,
    loading: { ...state.loading, update: { ...state.loading.update, threadUrgency: true } },
  })),

  on(smartInboxActions.UpdateThreadUrgencySuccess, (state, { conversationId, isUrgent }) => ({
    ...state,
    conversations: state.conversations.map((c) => (c.id === conversationId ? { ...c, is_urgent: isUrgent } : c)),
    activeConversation:
      state.activeConversation?.id === conversationId
        ? { ...state.activeConversation, is_urgent: isUrgent }
        : state.activeConversation,
    activeConversationDetails:
      state.activeConversationDetails?.id === conversationId
        ? { ...state.activeConversationDetails, is_urgent: isUrgent }
        : state.activeConversationDetails,
    loading: { ...state.loading, update: { ...state.loading.update, threadUrgency: false } },
  })),

  on(smartInboxActions.UpdateThreadUrgencyError, (state) => ({
    ...state,
    loading: { ...state.loading, update: { ...state.loading.update, threadUrgency: false } },
  })),

  // ─── Mark Read ────────────────────────────────────────────────────────────

  on(smartInboxActions.MarkConversationsRead, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      update: { ...state.loading.update, markRead: true },
    },
  })),

  on(smartInboxActions.MarkConversationsReadSuccess, (state, { conversationIds, isRead }) => ({
    ...state,
    conversations: state.conversations.map((c) =>
      conversationIds.includes(c.id) ? { ...c, is_read: isRead } : c
    ),
    activeConversation:
      state.activeConversation && conversationIds.includes(state.activeConversation.id)
        ? { ...state.activeConversation, is_read: isRead }
        : state.activeConversation,
    loading: {
      ...state.loading,
      update: { ...state.loading.update, markRead: false },
    },
  })),

  on(smartInboxActions.MarkConversationsReadError, (state) => ({
    ...state,
    loading: {
      ...state.loading,
      update: { ...state.loading.update, markRead: false },
    },
  })),

  // ─── Create Comment ─────────────────────────────────────────────────────────

  on(smartInboxActions.CreateCommentSuccess, (state, { object }) => ({
    ...state,
    activeConversationDetails: {
      ...state.activeConversationDetails!,
      objects: [...state.activeConversationDetails!.objects!, {
        ...object,
        object_type: TimelineObjectType.COMMENT,
      }],
    },
  })),

  // ─── Update / Delete Comment ───────────────────────────────────────────────

  on(smartInboxActions.UpdateCommentSuccess, (state, { conversationId, commentId, object }) => {
    if (!state.activeConversationDetails || state.activeConversationDetails.id !== conversationId) return state;
    const objects = (state.activeConversationDetails.objects ?? []).map((timelineObj: IConversationTimelineObject) => {
      if (timelineObj.object_type === TimelineObjectType.COMMENT && timelineObj.id === commentId) {
        return { ...timelineObj, ...object };
      }
      return timelineObj;
    });
    return {
      ...state,
      activeConversationDetails: { ...state.activeConversationDetails, objects },
    };
  }),

  on(smartInboxActions.DeleteCommentSuccess, (state, { conversationId, commentId }) => {
    if (!state.activeConversationDetails || state.activeConversationDetails.id !== conversationId) return state;
    const objects = (state.activeConversationDetails.objects ?? []).filter(
      (obj) => !(obj.object_type === TimelineObjectType.COMMENT && obj.id === commentId)
    );
    return {
      ...state,
      activeConversationDetails: { ...state.activeConversationDetails, objects },
    };
  }),

  // ─── Realtime Upsert ──────────────────────────────────────────────────────

  on(smartInboxActions.UpsertConversation, (state, { conversation }) => {
    const existingIndex = state.conversations.findIndex((c) => c.id === conversation.id);

    if (existingIndex !== -1) {
      const updated = [...state.conversations];
      updated[existingIndex] = conversation;
      return {
        ...state,
        conversations: updated,
        activeConversation:
          state.activeConversation?.id === conversation.id
            ? conversation
            : state.activeConversation,
      };
    }

    return {
      ...state,
      conversations: [conversation, ...state.conversations],
    };
  }),

  // ─── Send Compose ──────────────────────────────────────────────────────

  on(smartInboxActions.SendComposeSuccess, (state, { draftId }) => ({
    ...state,
    drafts: draftId ? state.drafts.filter((d) => d.id !== draftId) : state.drafts,
  })),

  // ─── Drafts (Load) ──────────────────────────────────────────────────────

  on(smartInboxActions.LoadDrafts, (state) => ({
    ...state,
    conversations: [],
    drafts: [],
    mentions: [],
    sentMessages: [],
    pagination: null,
    loading: { ...state.loading, get: { ...state.loading.get, conversations: true } },
  })),

  on(smartInboxActions.LoadDraftsSuccess, (state, { drafts }) => ({
    ...state,
    drafts: drafts.data,
    pagination: drafts.metadata,
    loading: { ...state.loading, get: { ...state.loading.get, conversations: false } },
  })),

  on(smartInboxActions.LoadDraftsError, (state) => ({
    ...state,
    drafts: [],
    loading: { ...state.loading, get: { ...state.loading.get, conversations: false } },
  })),

  // ─── Drafts (Load More) ───────────────────────────────────────────────

  on(smartInboxActions.LoadMoreDraftsSuccess, (state, { drafts }) => ({
    ...state,
    drafts: [...state.drafts, ...drafts.data],
    pagination: drafts.metadata,
  })),

  on(smartInboxActions.CreateDraftSuccess, (state, { draft }) => ({
    ...state,
    drafts: [...state.drafts, draft],
  })),

  on(smartInboxActions.UpdateDraftSuccess, (state, { draft }) => ({
    ...state,
    drafts: state.drafts.map((d) => (d.id === draft.id ? draft : d)),
  })),

  on(smartInboxActions.DeleteDraftSuccess, (state, { draftId }) => ({
    ...state,
    drafts: state.drafts.filter((d) => d.id !== draftId),
  })),

  // ─── Mentions (Load) ──────────────────────────────────────────────────────

  on(smartInboxActions.LoadMentions, (state) => ({
    ...state,
    conversations: [],
    drafts: [],
    mentions: [],
    sentMessages: [],
    pagination: null,
    loading: { ...state.loading, get: { ...state.loading.get, mentions: true } },
  })),

  on(smartInboxActions.LoadMentionsSuccess, (state, { mentions }) => ({
    ...state,
    mentions: mentions.data,
    pagination: mentions.metadata,
    loading: { ...state.loading, get: { ...state.loading.get, mentions: false } },
  })),

  on(smartInboxActions.LoadMentionsError, (state) => ({
    ...state,
    mentions: [],
    loading: { ...state.loading, get: { ...state.loading.get, mentions: false } },
  })),

  // ─── Mentions (Load More) ─────────────────────────────────────────────────

  on(smartInboxActions.LoadMoreMentionsSuccess, (state, { mentions }) => ({
    ...state,
    mentions: [...state.mentions, ...mentions.data],
    pagination: mentions.metadata,
  })),

  // ─── Sent Messages (Load) ─────────────────────────────────────────────────

  on(smartInboxActions.LoadSentMessages, (state) => ({
    ...state,
    conversations: [],
    drafts: [],
    mentions: [],
    sentMessages: [],
    pagination: null,
    selectedConversationIds: [],
    loading: { ...state.loading, get: { ...state.loading.get, conversations: true } },
  })),

  on(smartInboxActions.LoadSentMessagesSuccess, (state, { messages }) => ({
    ...state,
    sentMessages: messages.data,
    pagination: messages.metadata,
    loading: { ...state.loading, get: { ...state.loading.get, conversations: false } },
  })),

  on(smartInboxActions.LoadSentMessagesError, (state) => ({
    ...state,
    sentMessages: [],
    loading: { ...state.loading, get: { ...state.loading.get, conversations: false } },
  })),

  // ─── Sent Messages (Load More) ────────────────────────────────────────────

  on(smartInboxActions.LoadMoreSentMessagesSuccess, (state, { messages }) => ({
    ...state,
    sentMessages: [...state.sentMessages, ...messages.data],
    pagination: messages.metadata,
  })),

  // ─── Select sent message (open conversation + highlight that message) ──────

  on(smartInboxActions.SelectSentMessage, (state, { message }) => {
    const minimalConversation: IConversation = {
      id: message.thread_id,
      subject: message.subject || '',
      sender: message.sender_name || message.sender || '',
      sender_email: message.sender_email || '',
      mailbox: message.mailbox,
      provider_thread_id: '',
      last_message_at: message.timestamp || '',
      message_count: 0,
      attachment_count: message.attachment_count || 0,
      is_read: true,
      assigned_to_id: '',
      urgency: '',
      classification: ConversationClassification.UNDEFINED,
      status: ConversationStatus.NEW,
      assignee: null,
      customer: null,
    };
    return {
      ...state,
      activeConversation: minimalConversation,
      activeConversationDetails: null,
      activeCommentId: null,
      activeMessageId: message.id,
      loading: { ...state.loading, get: { ...state.loading.get, conversationDetails: true } },
    };
  }),

  on(smartInboxActions.SetActiveMessageId, (state, { messageId }) => ({
    ...state,
    activeMessageId: messageId,
  })),

  on(smartInboxActions.ClearActiveMessageId, (state) => ({
    ...state,
    activeMessageId: null,
  })),

  // ─── Select conversation from comment (sets active + activeCommentId for details endpoint) ─

  on(smartInboxActions.SelectConversationFromComment, (state, { mention }) => {
    const placeholderMailbox = { id: -1, name: '', email: '', type: MailboxType.PERSONAL };
    const minimalConversation: IConversation = {
      id: mention.thread_id,
      subject: mention.subject || '',
      sender: mention.author.name || '',
      sender_email: mention.author.email || '',
      mailbox: placeholderMailbox,
      provider_thread_id: '',
      last_message_at: mention.created_at || '',
      message_count: 0,
      attachment_count: 0,
      is_read: false,
      assigned_to_id: '',
      urgency: '',
      classification: mention.classification || ConversationClassification.UNDEFINED,
      status: mention.status || ConversationStatus.NEW,
      assignee: mention.assignee || null,
      customer: null,
    };
    return {
      ...state,
      activeConversation: minimalConversation,
      activeConversationDetails: null,
      activeCommentId: mention.id,
      activeMessageId: null,
      loading: { ...state.loading, get: { ...state.loading.get, conversationDetails: true } },
    };
  }),

  // ─── On Mention Received ───────────────────────────────────────────────

  on(smartInboxActions.CommentMentionReceived, (state) => ({
    ...state,
    foldersKPIs: state.foldersKPIs.map((f) => (f.id === InboxFolderType.MENTIONS ? { ...f, unread: f.unread + 1 } : f)),
  })),

  // ─── Create Order ──────────────────────────────────────────────────────────

  // Optimistic: show the skeleton on the active conversation right away. On the
  // next load the server's order_creation_in_progress flag keeps it armed
  // (survives a refresh, and re-opening the thread mid-job re-arms it) — no
  // in-memory list of threads to maintain.
  // OrderCreatedReceived has no reducer case: the effect refetches and the fresh
  // details carry the real order (flag now false) in the placeholder's place.
  on(smartInboxActions.CreateOrder, (state, { conversationId }) => ({
    ...state,
    activeConversationDetails: state.activeConversationDetails?.id === conversationId
      ? addOrderPlaceholder(state.activeConversationDetails)
      : state.activeConversationDetails,
  })),

  // Scoped to the failed thread: the user may well be reading another
  // conversation by now, and clearing blindly would strip *its* placeholder.
  on(smartInboxActions.CreateOrderError, (state, { conversationId }) => ({
    ...state,
    activeConversationDetails: withoutOrderPlaceholder(state.activeConversationDetails, conversationId),
  })),

  // ─── AI Draft ─────────────────────────────────────────────────────────────

  on(smartInboxActions.GenerateAiDraft, (state) => ({
    ...state,
    loading: { ...state.loading, create: { ...state.loading.create, aiDraft: true } },
  })),

  on(smartInboxActions.GenerateAiDraftSuccess, (state) => ({
    ...state,
    loading: { ...state.loading, create: { ...state.loading.create, aiDraft: false } },
  })),

  on(smartInboxActions.GenerateAiDraftError, (state) => ({
    ...state,
    loading: { ...state.loading, create: { ...state.loading.create, aiDraft: false } },
  })),
);

export function smartInboxReducer(state: SmartInboxState | undefined, action: Action) {
  return featureReducer(state, action);
}
